import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DeviceUnknownIcon from "@mui/icons-material/DeviceUnknown";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import MobileOffIcon from "@mui/icons-material/MobileOff";
import PermDeviceInformationIcon from "@mui/icons-material/PermDeviceInformation";
import { BarChart } from "@mui/x-charts/BarChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { PieChart } from "@mui/x-charts/PieChart";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { getOnlineNodeWidget } from "../../api/dashboardAPI";
import { getDevices } from "../../api/deviceAPI";
import { getNode, getOnlineNodes as getOnlineNodesList } from "../../api/nodeAPI";
import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { formatDateTime } from "../../utils/dateUtils";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const OFFLINE_LESS_24H = "offlineLess24h";
const OFFLINE_MORE_24H = "offlineMore24h";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: "primary",
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: "primary",
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

const normalizeTimestampMs = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  if (typeof value === "string") {
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue < 1e12 ? numericValue * 1000 : numericValue;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue < 1e12 ? numericValue * 1000 : numericValue;
};

const getLastOfflineRawValue = (record) => {
  if (!record || typeof record !== "object") {
    return null;
  }

  const preferredKeys = [
    "lastOffline",
    "lastoffline",
    "LastOffline",
    "lastOfflineEpoch",
    "lastofflineEpoch",
    "LastOfflineEpoch",
    "offlineEpoch",
    "OfflineEpoch",
    "lastDisconnect",
    "lastdisconnect",
    "LastDisconnect",
  ];

  for (const key of preferredKeys) {
    if (record[key] != null) {
      return record[key];
    }
  }

  const dynamicKey = Object.keys(record).find((key) =>
    key.toLowerCase().includes("lastoffline"),
  );

  return dynamicKey ? record[dynamicKey] : null;
};

const getOfflineCategory = (timestampMs) => {
  if (!timestampMs) {
    return null;
  }

  return Date.now() - timestampMs < TWENTY_FOUR_HOURS_MS
    ? OFFLINE_LESS_24H
    : OFFLINE_MORE_24H;
};

const getDeviceDisplayName = (device, t) => {
  return (
    device?.nameDevice ||
    device?.serialDevice ||
    (device?.idDevice != null
      ? `Device ${device.idDevice}`
      : t("dashboard.onlineOffline.unknownDevice", "Bilinmeyen cihaz"))
  );
};

const getNodeId = (device) => {
  const rawNodeId = device?.node_idNode ?? device?.nodeId ?? null;
  if (rawNodeId == null || rawNodeId === "") {
    return null;
  }

  const numericNodeId = Number(rawNodeId);
  return Number.isNaN(numericNodeId) ? rawNodeId : numericNodeId;
};

const isDeviceCurrentlyOffline = (device, onlineNodeIdSet) => {
  const nodeId = getNodeId(device);
  if (nodeId != null) {
    return !onlineNodeIdSet.has(nodeId);
  }

  const statusValue = device?.isOnline ?? device?.online ?? device?.is_online;
  return !(
    statusValue === 1 ||
    statusValue === true ||
    statusValue === "1" ||
    String(statusValue).toLowerCase() === "online"
  );
};

const getCategoryColor = (categoryKey) => {
  if (categoryKey === OFFLINE_LESS_24H) {
    return "#FFC107";
  }

  if (categoryKey === OFFLINE_MORE_24H) {
    return "#ff5252";
  }

  return "#9E9E9E";
};

const formatOfflineDuration = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) {
    return "0 dk";
  }

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return hours > 0 ? `${days} gün ${hours} sa` : `${days} gün`;
  }

  if (hours > 0) {
    return minutes > 0 ? `${hours} sa ${minutes} dk` : `${hours} sa`;
  }

  return `${minutes} dk`;
};

export default function ChartOnlineOffline() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isRunning } = useRefreshInterval();
  const isPageVisible = usePageVisibility();

  const [data, setData] = useState({
    onlineNodes: 0,
    lowerThen24offlineNodes: 0,
    greaterThen24offlineNodes: 0,
    totalNodes: 0,
    unAssignedDevices: 0,
  });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDialogTitle, setDetailDialogTitle] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [selectedDetailCategory, setSelectedDetailCategory] = useState(null);
  const intervalRef = useRef(null);

  const { loading, error, execute } = useApiRequest({
    handle401: true,
    onSuccess: (result) => {
      setData({
        onlineNodes: result.onlineNodes || 0,
        lowerThen24offlineNodes: result.lowerThen24offlineNodes || 0,
        greaterThen24offlineNodes: result.greaterThen24offlineNodes || 0,
        totalNodes: result.totalNodes || 0,
        unAssignedDevices: result.unAssignedDevices || 0,
      });
    },
  });

  useEffect(() => {
    if (!isPageVisible) {
      return;
    }

    execute(getOnlineNodeWidget, {
      showLoading: true,
      errorMessage: "Failed to fetch dashboard data",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRunning && isPageVisible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        execute(getOnlineNodeWidget, {
          showLoading: false,
          errorMessage: "Failed to fetch dashboard data",
        });
      }, 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, isPageVisible]);

  useEffect(() => {
    const handler = () => {
      if (!isPageVisible) {
        return;
      }

      execute(getOnlineNodeWidget, {
        showLoading: true,
        errorMessage: "Failed to fetch dashboard data",
      });
    };

    window.addEventListener("refreshAllData", handler);
    return () => window.removeEventListener("refreshAllData", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageVisible]);

  const {
    onlineNodes,
    lowerThen24offlineNodes,
    greaterThen24offlineNodes,
    totalNodes,
    unAssignedDevices,
  } = data;

  const computedTotal =
    onlineNodes + lowerThen24offlineNodes + greaterThen24offlineNodes;
  const total = totalNodes || computedTotal;

  const categoryLabels = useMemo(
    () => ({
      online: t("dashboard.onlineOffline.online"),
      [OFFLINE_LESS_24H]: t("dashboard.onlineOffline.offlineLess24h"),
      [OFFLINE_MORE_24H]: t("dashboard.onlineOffline.offlineMore24h"),
      unAssigned: t("dashboard.onlineOffline.unAssigned", "Atanmamış Terminal"),
    }),
    [t],
  );

  const chartData = useMemo(
    () =>
      [
        {
          id: "online",
          label: categoryLabels.online,
          value: onlineNodes,
          color: "#4CAF50",
        },
        {
          id: OFFLINE_LESS_24H,
          label: categoryLabels[OFFLINE_LESS_24H],
          value: lowerThen24offlineNodes,
          color: "#FFC107",
        },
        {
          id: OFFLINE_MORE_24H,
          label: categoryLabels[OFFLINE_MORE_24H],
          value: greaterThen24offlineNodes,
          color: "#ff5252",
        },
        {
          id: "unAssigned",
          label: categoryLabels.unAssigned,
          value: unAssignedDevices,
          color: "#9E9E9E",
        },
      ].filter((item) => item.value > 0),
    [
      categoryLabels,
      onlineNodes,
      lowerThen24offlineNodes,
      greaterThen24offlineNodes,
      unAssignedDevices,
    ],
  );

  const statsItems = useMemo(
    () => [
      {
        key: "online",
        name: categoryLabels.online,
        value: onlineNodes,
        color: "#4CAF50",
        icon: <MobileFriendlyIcon />,
      },
      {
        key: OFFLINE_LESS_24H,
        name: categoryLabels[OFFLINE_LESS_24H],
        value: lowerThen24offlineNodes,
        color: "#FFC107",
        icon: <PermDeviceInformationIcon />,
      },
      {
        key: OFFLINE_MORE_24H,
        name: categoryLabels[OFFLINE_MORE_24H],
        value: greaterThen24offlineNodes,
        color: "#ff5252",
        icon: <MobileOffIcon />,
      },
      {
        key: "unAssigned",
        name: categoryLabels.unAssigned,
        value: unAssignedDevices,
        color: "#9E9E9E",
        icon: <DeviceUnknownIcon />,
      },
    ],
    [
      categoryLabels,
      onlineNodes,
      lowerThen24offlineNodes,
      greaterThen24offlineNodes,
      unAssignedDevices,
    ],
  );

  const loadOfflineDetails = useCallback(
    async (categoryKey) => {
      if (
        categoryKey !== OFFLINE_LESS_24H &&
        categoryKey !== OFFLINE_MORE_24H
      ) {
        return;
      }

      setSelectedDetailCategory(categoryKey);
      setDetailDialogTitle(
        t("dashboard.onlineOffline.offlineDetailsTitle", {
          defaultValue: `${categoryLabels[categoryKey]} cihaz detayları`,
        }),
      );
      setDetailDialogOpen(true);
      setDetailLoading(true);
      setDetailError(null);
      setDetailItems([]);

      try {
        const [devicesResult, nodesResult, onlineNodesResult] = await Promise.all(
          [getDevices(), getNode(), getOnlineNodesList()],
        );

        const devices = devicesResult?.data || devicesResult || [];
        const nodes = nodesResult || [];
        const onlineNodeIdSet = new Set(
          (onlineNodesResult || []).map((node) => Number(node.idNode)),
        );
        const nodeMap = new Map(
          nodes.map((node) => [Number(node.idNode), node]),
        );

        const offlineDevices = devices
          .map((device) => {
            const nodeId = getNodeId(device);
            if (nodeId == null) {
              return null;
            }

            if (!isDeviceCurrentlyOffline(device, onlineNodeIdSet)) {
              return null;
            }

            const node = nodeMap.get(Number(nodeId));
            const lastOfflineMs = normalizeTimestampMs(
              getLastOfflineRawValue(device) ?? getLastOfflineRawValue(node),
            );

            if (!lastOfflineMs || getOfflineCategory(lastOfflineMs) !== categoryKey) {
              return null;
            }

            const offlineDurationMinutes = Math.max(
              1,
              Math.round((Date.now() - lastOfflineMs) / (60 * 1000)),
            );

            return {
              id:
                device.idDevice ??
                `${nodeId}-${device.serialDevice || device.nameDevice || "device"}`,
              nodeId,
              deviceName: getDeviceDisplayName(device, t),
              nodeName:
                node?.name ||
                t("dashboard.onlineOffline.unknownNode", "Bilinmeyen lokasyon"),
              lastOfflineMs,
              lastOfflineText: formatDateTime(new Date(lastOfflineMs)),
              offlineDurationMinutes,
              offlineDurationText: formatOfflineDuration(offlineDurationMinutes),
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            if (b.offlineDurationMinutes !== a.offlineDurationMinutes) {
              return b.offlineDurationMinutes - a.offlineDurationMinutes;
            }

            return b.lastOfflineMs - a.lastOfflineMs;
          });

        setDetailItems(offlineDevices);
      } catch (fetchError) {
        setDetailError(
          fetchError?.message ||
            t(
              "dashboard.onlineOffline.offlineDetailError",
              "Çevrimdışı detayları yüklenemedi.",
            ),
        );
      } finally {
        setDetailLoading(false);
      }
    },
    [categoryLabels, t],
  );

  const handlePieItemClick = useCallback(
    (_event, _identifier, item) => {
      if (!item?.id) {
        return;
      }

      void loadOfflineDetails(item.id);
    },
    [loadOfflineDetails],
  );

  const detailChartLabels = useMemo(
    () =>
      detailItems.map((item) =>
        item.deviceName.length > 22
          ? `${item.deviceName.slice(0, 22)}...`
          : item.deviceName,
      ),
    [detailItems],
  );

  const detailChartHeight = Math.min(
    Math.max(detailItems.length * 40, 220),
    520,
  );

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          flexGrow: 1,
          borderColor: "divider",
          borderRadius: 0,
          borderWidth: 1.5,
          overflow: "hidden",
          height: "100%",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PieChart
              skipAnimation
              margin={{
                left: 80,
                right: 80,
                top: 80,
                bottom: 80,
              }}
              tooltip={{ trigger: "none" }}
              onItemClick={handlePieItemClick}
              series={[
                {
                  data: chartData,
                  innerRadius: 65,
                  outerRadius: 80,
                  paddingAngle: 0,
                  highlightScope: { faded: "global", highlighted: "item" },
                },
              ]}
              height={160}
              width={200}
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                "& .MuiPieArc-root": {
                  cursor: "pointer",
                },
              }}
            >
              <PieCenterLabel
                primaryText={String(total)}
                secondaryText={t("dashboard.onlineOffline.title")}
              />
            </PieChart>
          </Box>

          {loading && (
            <Typography variant="body2" sx={{ textAlign: "center", py: 1 }}>
              {t("common.loading")}
            </Typography>
          )}

          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center", py: 1 }}
            >
              {error}
            </Typography>
          )}

          {statsItems.map((item) => {
            const isOfflineRow =
              item.key === OFFLINE_LESS_24H || item.key === OFFLINE_MORE_24H;

            return (
              <Stack
                key={item.key}
                direction="row"
                onClick={() => {
                  if (isOfflineRow && item.value > 0) {
                    void loadOfflineDetails(item.key);
                  }
                }}
                sx={{
                  alignItems: "center",
                  gap: 2,
                  pb: 2,
                  cursor: isOfflineRow && item.value > 0 ? "pointer" : "default",
                }}
              >
                {item.icon}
                <Stack sx={{ gap: 1, flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "500" }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {item.value}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    aria-label="Node status distribution"
                    value={total > 0 ? (item.value / total) * 100 : 0}
                    sx={{
                      [`& .${linearProgressClasses.bar}`]: {
                        backgroundColor: item.color,
                      },
                    }}
                  />
                </Stack>
              </Stack>
            );
          })}
        </CardContent>
      </Card>

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          {detailDialogTitle}
          <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {detailLoading && (
            <Typography variant="body2" sx={{ textAlign: "center", py: 2 }}>
              {t("common.loading")}
            </Typography>
          )}

          {detailError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center", py: 2 }}
            >
              {detailError}
            </Typography>
          )}

          {!detailLoading && !detailError && detailItems.length > 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                <BarChart
                  skipAnimation
                  layout="horizontal"
                  tooltip={{ trigger: "item" }}
                  axisHighlight={{ x: "none", y: "none" }}
                  yAxis={[
                    {
                      scaleType: "band",
                      data: detailChartLabels,
                      tickLabelStyle: { fontSize: 11 },
                    },
                  ]}
                  xAxis={[
                    {
                      label: t(
                        "dashboard.onlineOffline.offlineDuration",
                        "Çevrimdışı süre",
                      ),
                      valueFormatter: (value) =>
                        formatOfflineDuration(Math.max(0, Math.round(value || 0))),
                    },
                  ]}
                  series={[
                    {
                      data: detailItems.map((item) => item.offlineDurationMinutes),
                      color: getCategoryColor(selectedDetailCategory),
                      valueFormatter: (value) =>
                        formatOfflineDuration(Math.max(0, Math.round(value || 0))),
                    },
                  ]}
                  margin={{ left: 120, right: 20, top: 20, bottom: 40 }}
                  height={detailChartHeight}
                  slotProps={{ legend: { hidden: true } }}
                />
              </Box>

              <List dense disablePadding>
                {detailItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => {
                      if (item.nodeId != null) {
                        navigate(`/places/${item.nodeId}`);
                      }
                    }}
                    sx={{ py: 1 }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: getCategoryColor(selectedDetailCategory),
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={item.deviceName}
                      secondary={`${item.nodeName} | ${t(
                        "dashboard.onlineOffline.offlineDuration",
                        "Çevrimdışı süre",
                      )}: ${item.offlineDurationText} | ${t(
                        "dashboard.onlineOffline.lastOffline",
                        "Son çevrimdışı zamanı",
                      )}: ${item.lastOfflineText}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {!detailLoading && !detailError && detailItems.length === 0 && (
            <Typography variant="body2" sx={{ textAlign: "center", py: 2 }}>
              {t(
                "dashboard.onlineOffline.noOfflineData",
                "Bu kategori için lastOffline verisi bulunamadı.",
              )}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
